import type { ReactNode } from 'react';
import { useThemeChoice, type ThemeChoice } from '@/hooks/theme';

/*
 * The theme control, in the app bar beside the account details.
 *
 * Three buttons rather than one that cycles: "follow the OS" is the default and
 * the only one of the three a user would not think to look for, and a cycling
 * button never shows that it exists. Toggle semantics (aria-pressed) rather than
 * radios, because radios owe the user arrow-key navigation and a roving tabindex
 * — worth it for a form, not for three buttons that each do their thing on press.
 *
 * The icons are inline SVG for the same reason the rest of the portal has no icon
 * font: one more network dependency, and a glyph that arrives late, for three
 * shapes. Each button still carries its name as text — clipped, not removed, so
 * the control is operable by voice and readable by a screen reader.
 */

const SUN = (
  <svg viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
    <circle cx="8" cy="8" r="3.1" />
    <path d="M8 .9v1.8M8 13.3v1.8M2 2l1.3 1.3M12.7 12.7 14 14M.9 8h1.8M13.3 8h1.8M2 14l1.3-1.3M12.7 3.3 14 2" />
  </svg>
);

const MOON = (
  <svg viewBox="0 0 16 16" width="15" height="15" fill="currentColor" aria-hidden="true">
    <path d="M13.6 10.3A6.2 6.2 0 0 1 5.7 2.4a6.2 6.2 0 1 0 7.9 7.9Z" />
  </svg>
);

const SYSTEM = (
  <svg viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="1.6" y="2.4" width="12.8" height="9" rx="1.4" />
    <path d="M5.8 13.6h4.4" />
  </svg>
);

const OPTIONS: { value: ThemeChoice; label: string; icon: ReactNode }[] = [
  { value: 'system', label: 'Match my device', icon: SYSTEM },
  { value: 'light', label: 'Light theme', icon: SUN },
  { value: 'dark', label: 'Dark theme', icon: MOON }
];

export function ThemeToggle() {
  const [choice, setChoice] = useThemeChoice();

  return (
    <div className="theme-toggle" role="group" aria-label="Colour theme">
      {OPTIONS.map(option => (
        <button
          key={option.value}
          type="button"
          title={option.label}
          aria-pressed={choice === option.value}
          className={choice === option.value ? 'theme-option on' : 'theme-option'}
          onClick={() => setChoice(option.value)}>
          {option.icon}
          <span className="visually-hidden">{option.label}</span>
        </button>
      ))}
    </div>
  );
}
